import { SummaryItem as SummaryItemType } from '@renderer/chat/types'
import { SummaryTitle } from './nested/summary-title'
import { SummaryBody } from './nested/summary-body'
import { SummaryEditor } from './nested/summary-editor'
import { SummaryActionsBar } from './nested/summary-actions-bar'
import { ChatAction } from './nested/summary-actions'
import { SummaryFooter } from './nested/summary-footer'
import * as React from 'react'
import { useState, useEffect } from 'react'
import { SummaryProvider } from './nested/summary-provider'
import { ActionPopover } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { useSummaryMarkAsRead } from './hooks/use-summary-mark-as-read'

/**
 * Summary Component
 *
 * A comprehensive summary display component with expand/collapse, editing,
 * artifacts, activity tracking, and feedback collection.
 *
 * @example
 * // Basic usage - component manages its own expand state
 * <Summary summary={summaryObject} />
 *
 * @example
 * // Using sub-components independently with SummaryProvider
 * <SummaryProvider summary={summary}>
 *   <Summary.Title />
 *   <Summary.ActivityBadges />
 *   <Summary.Body />
 *   <Summary.ActionsBar />
 * </SummaryProvider>
 *
 * @example
 * // Using action components
 * <Summary.Actions.Regenerate onRegenerate={handleRegenerate} />
 * <Summary.Actions.Edit onClick={handleEdit} />
 * <Summary.Actions.Copy onCopy={handleCopy} />
 */

type SummaryProps = React.ComponentProps<'div'> & {
  summary: SummaryItemType
  forceExpanded?: boolean
}

function Summary({ summary, forceExpanded = false, children, className, ...props }: SummaryProps) {
  const [isExpanded, setExpanded] = useState(forceExpanded)
  const [isEditing, setEditing] = useState(false)
  const [edited, setEdited] = useState<string | undefined>(undefined)
  const [isHovered, setIsHovered] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)

  // Reset editing and interaction state when summary changes
  useEffect(() => {
    setEdited(undefined)
    setEditing(false)
    setHasInteracted(false)
  }, [summary.id])

  // Track interactions (hover or click)
  const handleInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true)
    }
  }

  // Handle collapse/expand on click
  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      // Don't allow collapse/expand when editing
      if (isEditing) return

      const target = e.target as HTMLElement

      // Exclude interactive elements (buttons, links, inputs, etc.)
      const isInteractive =
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('input') ||
        target.closest('textarea')

      if (isInteractive) return

      if (!isExpanded) {
        // When collapsed, expand on click
        setExpanded(true)
      } else {
        // When expanded, only allow collapse if clicking outside body/editor
        // Check if click is within body or editor
        const isWithinBody = target.closest('[data-summary-body]') !== null
        const isWithinEditor = target.closest('[contenteditable="true"]') !== null

        if (!isWithinBody && !isWithinEditor) {
          // Clicked outside body/editor, collapse
          setExpanded(false)
        }
      }
    },
    [isExpanded, setExpanded, isEditing],
  )

  // Separate ActionsBar from other children
  const actionsBar = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === SummaryActionsBar,
  )
  const otherChildren = React.Children.toArray(children).filter(
    (child) => !(React.isValidElement(child) && child.type === SummaryActionsBar),
  )

  const popoverOpen = isEditing || isGenerating || isHovered

  return (
    <SummaryProvider
      summary={summary}
      isExpanded={isExpanded}
      setExpanded={setExpanded}
      isEditing={isEditing}
      setEditing={setEditing}
      edited={edited}
      setEdited={setEdited}
      isGenerating={isGenerating}
      setIsGenerating={setIsGenerating}
      hasInteracted={hasInteracted}
    >
      <MarkAsReadHandler forceExpanded={forceExpanded} />
      <div
        className={twMerge(className)}
        onMouseEnter={handleInteraction}
        onClick={(e) => {
          handleInteraction()
          handleClick(e)
        }}
        {...props}
      >
        <ActionPopover open={popoverOpen} onOpenChange={setIsHovered}>
          <ActionPopover.Trigger
            render={(props) => <div {...props} />}
            nativeButton={false}
            className="flex flex-col gap-3"
          >
            {otherChildren}
          </ActionPopover.Trigger>
          {actionsBar && <ActionPopover.Content collisionPadding={130}>{actionsBar}</ActionPopover.Content>}
        </ActionPopover>
      </div>
    </SummaryProvider>
  )
}

function MarkAsReadHandler({ forceExpanded }: { forceExpanded: boolean }) {
  useSummaryMarkAsRead(forceExpanded)
  return null
}

// Attach sub-components to the Summary namespace
Summary.Provider = SummaryProvider
Summary.Title = SummaryTitle
Summary.Body = SummaryBody
Summary.Editor = SummaryEditor
Summary.ActionsBar = SummaryActionsBar
Summary.Footer = SummaryFooter
Summary.ActionPopover = ActionPopover
Summary.ChatAction = ChatAction

export { Summary }
